import { useCallback, useEffect, useMemo, useRef } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import ListEntitiesItem from "components/ListEntitiesItem";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Toggle from "ds/components/Toggle";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import useSetWorkerDrain from "shared/WorkerPool/useSetWorkerDrain";
import { Action, Worker, WorkerPool } from "types/generated";
import Timestamp from "components/Timestamp";
import { checkAllowedAction } from "utils/access";

import PrivateWorkerPoolMetaTagsList from "../../MetaTagsList";
import { COLUMN_GAP, COLUMN_ORDER } from "../constants";
import WorkerStatusBadge from "../StatusBadge";
import styles from "./styles.module.css";

export type PrivateWorkerPoolWorkersListItemProps = {
  worker: Worker;
  workerPool: WorkerPool;
  setRowHeight?: (size: number) => void;
  checked: boolean;
  onCheckItem: (workerId: string, isSelected: boolean) => void;
};

const PrivateWorkerPoolWorkersListItem = ({
  worker,
  workerPool,
  setRowHeight,
  checked,
  onCheckItem,
}: PrivateWorkerPoolWorkersListItemProps) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const lastReportedHeightRef = useRef<number | undefined>(undefined);

  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const drainAccessResult = checkAllowedAction(workerPool, Action.WorkerDrainSet);

  const [setWorkerDrain, { loading }] = useSetWorkerDrain({
    refetchQueries: ["SearchPrivateWorkerPoolWorkers"],
  });

  const handleDrain = () => {
    setWorkerDrain({ workerPool: workerPool.id, drain: true, id: worker.id }, (data) => {
      if (data?.workerDrainSet) {
        reportSuccess({ message: `Worker ${data.workerDrainSet.id} has been drained` });
      }
    });
  };

  const handleUndrain = () => {
    setWorkerDrain({ workerPool: workerPool.id, drain: false, id: worker.id }, (data) => {
      if (data?.workerDrainSet) {
        reportSuccess({ message: `Worker ${data.workerDrainSet.id} has been undrained` });
      }
    });
  };

  const parseMetadata = useCallback(
    (metadata: string) => {
      try {
        return JSON.parse(metadata) as Record<string, string>;
      } catch (error) {
        onError(error);
        return undefined;
      }
    },
    [onError]
  );

  const metadata = useMemo(() => {
    const parsedMetadata = (worker.metadata && parseMetadata(worker.metadata)) || {};

    return Object.entries(parsedMetadata).map(([key, value]) => `${key}: ${value}`);
  }, [parseMetadata, worker.metadata]);

  const handleRowHeight = () => {
    if (!setRowHeight || !rowRef.current) return;

    const height = Math.round(rowRef.current.getBoundingClientRect().height);

    // Skip redundant reports: react-window's resetAfterIndex on every measurement
    // is expensive and, for an unchanged row, only adds jitter.
    if (lastReportedHeightRef.current === height) return;

    lastReportedHeightRef.current = height;
    setRowHeight(height);
  };

  // on every render, update the row height
  useEffect(handleRowHeight);

  const checkboxAriaLabel = useCallback(
    (checked: boolean) =>
      checked ? `Unselect ${worker.id} worker pool` : `Select ${worker.id} worker pool`,
    [worker.id]
  );

  return (
    <ListEntitiesItem
      direction="row"
      align="start"
      justify="between"
      grid
      gridTemplate={COLUMN_ORDER}
      gap={`0 ${COLUMN_GAP}`}
      ref={rowRef}
    >
      <Box direction="row" align="center" className={styles.fixedHeightRow}>
        <Toggle
          variant="checkbox"
          id={worker.id}
          isSelected={checked}
          onChange={(isSelected) => onCheckItem(worker.id, isSelected)}
          aria-label={checkboxAriaLabel(checked)}
        />
      </Box>

      <Box direction="row" align="center" className={styles.fixedHeightRow}>
        <WorkerStatusBadge status={worker.status} />
      </Box>

      <Box direction="row" align="center" className={styles.fixedHeightRow}>
        <Typography tag="span" variant="p-body3">
          {worker.id}
        </Typography>
      </Box>

      <PrivateWorkerPoolMetaTagsList tags={metadata} onExpand={handleRowHeight} />

      <Box direction="row" align="center" className={styles.fixedHeightRow}>
        <Timestamp timestamp={worker.createdAt} />
      </Box>

      <Box direction="row" align="center" justify="end" className={styles.fixedHeightRow}>
        {worker.drained ? (
          <Tooltip
            on={(props) => (
              <Button
                {...props}
                onPress={handleUndrain}
                loading={loading}
                disabled={loading || !drainAccessResult.result}
                variant="secondary"
                size="small"
              >
                Undrain
              </Button>
            )}
          >
            {drainAccessResult.result
              ? "Enable scheduling future runs on this worker"
              : drainAccessResult.message}
          </Tooltip>
        ) : (
          <Tooltip
            on={(props) => (
              <Button
                {...props}
                onPress={handleDrain}
                loading={loading}
                disabled={loading || !drainAccessResult.result}
                variant="dangerPrimary"
                size="small"
              >
                Drain
              </Button>
            )}
          >
            {drainAccessResult.result
              ? "Disable scheduling future runs on this worker"
              : drainAccessResult.message}
          </Tooltip>
        )}
      </Box>
    </ListEntitiesItem>
  );
};

export default PrivateWorkerPoolWorkersListItem;
